import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import PageHeader from '../components/PageHeader.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import Modal from '../components/Modal.jsx';
import ApiKeyRevealModal from '../components/ApiKeyRevealModal.jsx';

const STATUSES = ['active', 'paused', 'revoked'];

// Matches SeatBundleFloorsPage.jsx's own PAGE_SIZE/pagination pattern. GET /:seatId used to embed
// every source for the seat, unbounded -- fine when every seat had a handful of manually-added
// mappings, but it started timing out for real once Pumpkin Ads' bulk sourcesOnly import passed
// ~280k rows (2026-09-04 production incident, caught live while running that exact import). The
// seat-detail call now only asks for a count (sourcesCount); this page paginates/searches the
// actual rows through GET /:seatId/sources, same as the bundle-floors page already does.
const PAGE_SIZE = 100;

export default function SeatDetailPage() {
  const { seatId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [seat, setSeat] = useState(null);
  const [sources, setSources] = useState({ total: 0, rows: [] });
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [showAddSource, setShowAddSource] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [sampleRateInput, setSampleRateInput] = useState('100');
  const [sampleRateBusy, setSampleRateBusy] = useState(false);

  const totalPages = Math.max(1, Math.ceil(sources.total / PAGE_SIZE));

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [detail, pubs] = await Promise.all([api.getInboundSeat(seatId), api.listPublishers().catch(() => [])]);
      setSeat(detail);
      setPublishers(pubs);
      setSampleRateInput(String(detail.sampleRatePct ?? 100));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load seat.');
    } finally {
      setLoading(false);
    }
  }

  async function loadSources() {
    setSourcesLoading(true);
    try {
      const result = await api.listSeatSources(seatId, { limit: PAGE_SIZE, offset: page * PAGE_SIZE, search });
      setSources(result);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not load sources.');
    } finally {
      setSourcesLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [seatId]);

  useEffect(() => {
    loadSources();
  }, [seatId, page, search]);

  async function refreshSources() {
    await loadSources();
  }

  async function changeStatus(nextStatus) {
    setStatusBusy(true);
    try {
      const updated = await api.patchInboundSeat(seatId, { status: nextStatus });
      setSeat((prev) => ({ ...prev, ...updated }));
      toast.success(`Seat marked ${nextStatus}.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update seat status.');
    } finally {
      setStatusBusy(false);
    }
  }

  async function saveSampleRate() {
    const pct = Number(sampleRateInput);
    if (!Number.isInteger(pct) || pct < 0 || pct > 100) {
      toast.error('Sampling rate must be a whole number between 0 and 100.');
      return;
    }
    setSampleRateBusy(true);
    try {
      const updated = await api.patchInboundSeat(seatId, { sampleRatePct: pct });
      setSeat((prev) => ({ ...prev, ...updated }));
      toast.success(pct === 100 ? 'Sampling disabled -- all traffic admitted.' : `Now admitting ~${pct}% of this seat's traffic.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update sampling rate.');
    } finally {
      setSampleRateBusy(false);
    }
  }

  async function rotateKey() {
    if (!window.confirm('Rotate this seat\'s API key? The old key will stop working immediately.')) return;
    setRotating(true);
    try {
      const result = await api.rotateInboundSeatKey(seatId);
      setReveal(result);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not rotate key.');
    } finally {
      setRotating(false);
    }
  }

  async function removeSource(source) {
    if (!window.confirm(`Remove source ${source.bundleId || source.domain}?`)) return;
    try {
      await api.deleteSeatSource(source.id);
      toast.success('Source removed.');
      refreshSources();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove source.');
    }
  }

  async function toggleSourceStatus(source) {
    const next = source.status === 'active' ? 'paused' : 'active';
    try {
      await api.patchSeatSource(source.id, { status: next });
      refreshSources();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update source.');
    }
  }

  if (loading) return <div className="page"><LoadingState label="Loading seat" /></div>;
  if (error || !seat) {
    return (
      <div className="page">
        <EmptyState title="Seat not found" message={error || 'This seat may have been deleted.'} />
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title={
          <span className="page-header__title-with-badge">
            {seat.name}
            <StatusBadge status={seat.status} />
          </span>
        }
        subtitle={<code className="code-chip">{seat.seatId}</code>}
        actions={
          <>
            <button className="btn btn--ghost" onClick={() => navigate(`/inbound-seats/${seatId}/bundle-floors`)}>
              Bundle floors &amp; bulk import
            </button>
            <button className="btn btn--ghost" onClick={() => navigate('/inbound-seats')}>
              &larr; All seats
            </button>
          </>
        }
      />

      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Seat controls</h2>
        </div>
        <div className="button-row">
          {STATUSES.map((s) => (
            <button
              key={s}
              className={s === 'revoked' ? 'btn btn--danger-ghost' : 'btn btn--ghost'}
              disabled={statusBusy || seat.status === s}
              onClick={() => changeStatus(s)}
            >
              {s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
          <button className="btn btn--primary" disabled={rotating} onClick={rotateKey}>
            {rotating ? 'Rotating…' : 'Rotate API key'}
          </button>
        </div>
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Traffic sampling</h2>
        </div>
        <p className="card__hint">
          Admit only a percentage of this seat's inbound requests -- the rest are turned away
          cheaply, before any publisher resolution or auction runs. Use this to safely ramp up a
          new, high-volume supply source (e.g. a bulk bundle-inventory onboarding) without
          risking the current infrastructure. 100 = no sampling, every request runs a real
          auction.
        </p>
        <div className="button-row">
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            value={sampleRateInput}
            onChange={(e) => setSampleRateInput(e.target.value)}
            style={{ width: '5rem' }}
          />
          <span>% of traffic admitted</span>
          <button className="btn btn--primary" disabled={sampleRateBusy} onClick={saveSampleRate}>
            {sampleRateBusy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </section>

      <section className="card card--flush">
        <div className="card__header card__header--padded">
          <h2 className="card__title">Sources ({sources.total.toLocaleString()})</h2>
          <div className="button-row">
            <input
              placeholder="Search bundle/domain…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); setSearch(searchInput.trim()); } }}
            />
            <button className="btn btn--ghost btn--sm" onClick={() => { setPage(0); setSearch(searchInput.trim()); }}>
              Search
            </button>
            <button className="btn btn--primary btn--sm" onClick={() => setShowAddSource(true)}>
              + Add source
            </button>
          </div>
        </div>
        {sourcesLoading ? (
          <div className="card__padded"><LoadingState label="Loading sources" /></div>
        ) : sources.rows.length === 0 ? (
          <div className="card__padded">
            <EmptyState
              title={search ? 'No sources match that search' : 'No sources yet'}
              message={
                search
                  ? 'Try a different bundle ID or domain.'
                  : 'Map an app bundle ID or site domain to a BidDigi publisher so inbound requests resolve correctly.'
              }
              action={
                !search && (
                  <button className="btn btn--primary" onClick={() => setShowAddSource(true)}>
                    + Add source
                  </button>
                )
              }
            />
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Publisher</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sources.rows.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <code className="code-chip">{s.bundleId || s.domain}</code>
                        <div className="cell-subtitle">{s.bundleId ? 'App bundle ID' : 'Site domain'}</div>
                      </td>
                      <td>{publisherName(publishers, s.publisherId)}</td>
                      <td>
                        <label className="switch">
                          <input type="checkbox" checked={s.status === 'active'} onChange={() => toggleSourceStatus(s)} />
                          <span className="switch__track">
                            <span className="switch__thumb" />
                          </span>
                        </label>
                      </td>
                      <td>
                        <button className="btn btn--danger-ghost btn--sm" onClick={() => removeSource(s)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="button-row card__padded">
                <button className="btn btn--ghost btn--sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>&larr; Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn--ghost btn--sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next &rarr;</button>
              </div>
            )}
          </>
        )}
      </section>

      {showAddSource && (
        <AddSourceModal
          publishers={publishers}
          onClose={() => setShowAddSource(false)}
          onCreated={() => {
            setShowAddSource(false);
            refreshSources();
          }}
          seatId={seatId}
        />
      )}

      {reveal && (
        <ApiKeyRevealModal title={`New API key for "${seat.name}"`} apiKey={reveal.apiKey} onClose={() => setReveal(null)} />
      )}
    </div>
  );
}

function publisherName(publishers, id) {
  const pub = publishers.find((p) => p.id === id);
  return pub ? pub.name : id;
}

function AddSourceModal({ publishers, onClose, onCreated, seatId }) {
  const toast = useToast();
  const [publisherId, setPublisherId] = useState(publishers[0]?.id || '');
  const [sourceType, setSourceType] = useState('bundleId');
  const [sourceValue, setSourceValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    if (!publisherId) {
      setFormError('Select a publisher.');
      return;
    }
    if (!sourceValue.trim()) {
      setFormError(sourceType === 'bundleId' ? 'Enter an app bundle ID.' : 'Enter a site domain.');
      return;
    }
    setSaving(true);
    try {
      const body = { publisherId, [sourceType]: sourceValue.trim() };
      await api.createSeatSource(seatId, body);
      toast.success('Source added.');
      onCreated();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not add source.';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Add source" onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        <label className="field">
          <span className="field__label">Publisher</span>
          <select value={publisherId} onChange={(e) => setPublisherId(e.target.value)}>
            {publishers.length === 0 && <option value="">No publishers available</option>}
            {publishers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.domain})
              </option>
            ))}
          </select>
        </label>
        <div className="field">
          <span className="field__label">Source type</span>
          <div className="checkbox-row">
            <label className="field field--checkbox field--checkbox-inline">
              <input type="radio" name="sourceType" checked={sourceType === 'bundleId'} onChange={() => setSourceType('bundleId')} />
              <span>App bundle ID</span>
            </label>
            <label className="field field--checkbox field--checkbox-inline">
              <input type="radio" name="sourceType" checked={sourceType === 'domain'} onChange={() => setSourceType('domain')} />
              <span>Site domain</span>
            </label>
          </div>
        </div>
        <label className="field">
          <span className="field__label">{sourceType === 'bundleId' ? 'Bundle ID' : 'Domain'}</span>
          <input
            value={sourceValue}
            onChange={(e) => setSourceValue(e.target.value)}
            placeholder={sourceType === 'bundleId' ? 'com.acme.newsapp' : 'acmenews.com'}
          />
        </label>
        {formError && <div className="form-error">{formError}</div>}
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? 'Adding…' : 'Add source'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
